import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { getOwner } from '@ember/application';
import BaseController from 'uplisting-frontend/pods/base/controller';
import { animatedTransition } from 'uplisting-frontend/utils/router';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type { GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import MonthlyCalendarController from 'uplisting-frontend/pods/monthly/controller';

type ModelType =
  | ModelRegistry['bulk-closed-for-arrival']
  | ModelRegistry['bulk-closed-for-departure']
  | GenericChangeset<ModelRegistry['bulk-mlos']>
  | GenericChangeset<ModelRegistry['bulk-rate']>;

export default abstract class MonthlyEventBaseController extends BaseController {
  @service analytics!: Services['analytics'];

  @service('calendar/manager') calendarManager!: Services['calendar/manager'];

  @tracked isSubmitting = false;
  @tracked showConfirmModal = false;

  abstract handleSave(): Promise<void>;

  @cached
  get isCalendarRoute(): boolean {
    return this.currentRouteName.startsWith('calendar');
  }

  @cached
  get stepBackRoute(): string {
    return this.isCalendarRoute ? 'calendar.event' : 'monthly.event.index';
  }

  @action
  handleCancel(): void {
    animatedTransition(() => this.router.transitionTo(this.stepBackRoute));
  }

  @action
  handleClickSave(): void {
    if (this.isCalendarRoute) {
      this.handleSave();

      return;
    }

    const controller = getOwner(this)?.lookup('controller:monthly');

    if (!controller) {
      return;
    }

    if ((controller as MonthlyCalendarController).multiUnit) {
      this.showConfirmModal = true;
    } else {
      this.handleSave();
    }
  }

  protected assignDaysTo(model: ModelType): void {
    const value = model.sunday;

    Object.assign(model, {
      monday: value,
      tuesday: value,
      wednesday: value,
      thursday: value,
      friday: value,
      saturday: value,
    });
  }
}
